import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AEMErrorMessage } from '@com-delta-idp/mnx-skymiles-shared';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { StateFacade as OverviewFacade } from '../../facades/state.facade';
import { ErrorConstant, PLACE_HOLDER, SECTION_NAME } from '../../models';
import { ErrorDetails } from '../../models/api/content-api.interfaces';
import { DataLayerAnalyticsService } from '../../services/analytics/data-layer.service';
import { DocsService } from '../../services/api/docs.service';
import * as OverviewActions from '../actions/api.actions';

@Injectable()
export class DocsEffects {
  loadVouchersData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.loadVouchers),
      switchMap(() =>
        combineLatest([
          this.overview.flags$.pipe(
            filter((flags) => !!flags?.upgrades),
            take(1)
          ),
          this.overview.aemErrorMessages$.pipe(
            filter((aemErrorMessages) => aemErrorMessages !== undefined),
            take(1)
          )
        ]).pipe(
          switchMap(([, aemErrorMessages]) =>
            this.docs.getVouchersResponse().pipe(
              map((content) =>
                OverviewActions.loadVouchersSuccess({
                  vouchers: {
                    id: 'vouchers-content',
                    vouchersContent: content
                  }
                })
              ),
              catchError((error) => {
                this.dispatchError(error, aemErrorMessages);
                return of(
                  OverviewActions.loadVouchersFailure({
                    error: {
                      id: 'vouchers-content-error',
                      vouchersContentError: 'voucher'
                    }
                  })
                );
              })
            )
          )
        )
      )
    )
  );

  loadEDocsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OverviewActions.loadEDocs),
      switchMap(() =>
        combineLatest([
          this.overview.flags$.pipe(
            filter((flags) => !!flags?.upgrades),
            take(1)
          ),
          this.overview.eDocsRequest$.pipe(
            filter((eDocsRequest) => !!eDocsRequest),
            take(1)
          ),
          this.overview.aemErrorMessages$.pipe(
            filter((aemErrorMessages) => aemErrorMessages !== undefined),
            take(1)
          )
        ]).pipe(
          switchMap(([, payload, aemErrorMessages]) =>
            this.docs.getEDocsResponse(payload).pipe(
              map((content) =>
                OverviewActions.loadEDocsSuccess({
                  eDocs: {
                    id: 'eDocs-content',
                    eDocsContent: content
                  }
                })
              ),
              catchError((error) => {
                this.dispatchError(error, aemErrorMessages);
                return of(
                  OverviewActions.loadEDocsFailure({
                    error: {
                      id: 'eDocs-content-error',
                      eDocsContentError: 'eDocs'
                    }
                  })
                );
              })
            )
          )
        )
      )
    )
  );

  dispatchError = (
    error: HttpErrorResponse | ErrorDetails,
    aemErrorMessages: AEMErrorMessage[]
  ) => {
    const message = aemErrorMessages.find((item) => {
      return item.component === 'upgrades';
    });
    this.dataLayer.setErrorData(
      error,
      message.errorMessageDescription,
      ErrorConstant.EVENT_NAME.replace(
        PLACE_HOLDER,
        SECTION_NAME.VOUCHER_ECREDIT
      ),
      ErrorConstant.TYPE.replace(PLACE_HOLDER, SECTION_NAME.VOUCHER_ECREDIT)
    );
  };

  constructor(
    private readonly actions$: Actions,
    private readonly docs: DocsService,
    private readonly overview: OverviewFacade,
    protected dataLayer: DataLayerAnalyticsService
  ) {}
}
